import React, { useState, useEffect } from 'react';
import { X, Trophy } from 'lucide-react';
import { competitionService } from '../api/competitionsApi';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const defaultForm = {
  name: '',
  description: '',
  fundingAmount: 0,
  currency: 'USD',
  organization: '',
  applicationDeadline: '',
  requirements: [],
  status: 'open',
  eligibilityCriteria: '',
  maxApplications: '',
};

const inp = `w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-all
  focus:ring-2`;
const inpStyle = {
  borderColor: SYSTEM_COLORS.border,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};

const Label = ({ children }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
    {children}
  </label>
);

const CompetitionModal = ({ isOpen, onClose, competition }) => {
  const [form, setForm] = useState(defaultForm);
  const [reqInput, setReqInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (competition) {
      const deadline = competition.applicationDeadline;
      const formatted = deadline ? new Date(deadline).toISOString().slice(0, 16) : '';
      setForm({ ...defaultForm, ...competition, applicationDeadline: formatted });
    } else {
      setForm(defaultForm);
    }
  }, [competition]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleAddRequirement = () => {
    if (reqInput.trim()) {
      setForm(f => ({ ...f, requirements: [...(f.requirements || []), reqInput.trim()] }));
      setReqInput('');
    }
  };

  const handleRemoveRequirement = (i) => {
    setForm(f => ({ ...f, requirements: f.requirements.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organization || !form.applicationDeadline) {
      toast.error('Name, Organization, and Deadline are required');
      return;
    }
    setLoading(true);
    try {
      if (competition) {
        await competitionService.updateCompetition(competition.id, form);
        toast.success('Competition updated');
      } else {
        await competitionService.createCompetition(form);
        toast.success('Competition created');
      }
      onClose();
    } catch {
      toast.error('Error saving competition');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92dvh]"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, boxShadow: SYSTEM_COLORS.shadowStrong }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <Trophy className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
            </div>
            <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
              {competition ? 'Edit Competition' : 'New Competition'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:opacity-70"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Name *</Label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                  required
                />
              </div>

              <div>
                <Label>Organization *</Label>
                <input
                  type="text"
                  name="organization"
                  value={form.organization}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                  required
                />
              </div>

              <div>
                <Label>Status</Label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="awarded">Awarded</option>
                </select>
              </div>

              <div>
                <Label>Funding Amount</Label>
                <input
                  type="number"
                  name="fundingAmount"
                  value={form.fundingAmount}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                  min="0"
                />
              </div>

              <div>
                <Label>Currency</Label>
                <input
                  type="text"
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Application Deadline *</Label>
                <input
                  type="datetime-local"
                  name="applicationDeadline"
                  value={form.applicationDeadline}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Description</Label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  className={inp}
                  style={inpStyle}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Eligibility Criteria</Label>
                <input
                  type="text"
                  name="eligibilityCriteria"
                  value={form.eligibilityCriteria}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                />
              </div>

              <div>
                <Label>Max Applications</Label>
                <input
                  type="number"
                  name="maxApplications"
                  value={form.maxApplications}
                  onChange={handleChange}
                  className={inp}
                  style={inpStyle}
                  min="1"
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Requirements</Label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={reqInput}
                    onChange={e => setReqInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddRequirement())}
                    placeholder="Add requirement and press Enter…"
                    className={inp}
                    style={inpStyle}
                  />
                  <button
                    type="button"
                    onClick={handleAddRequirement}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold flex-shrink-0 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
                  >
                    Add
                  </button>
                </div>
                {form.requirements.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.requirements.map((req, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted, color: SYSTEM_COLORS.textMuted }}
                      >
                        {req}
                        <button
                          type="button"
                          onClick={() => handleRemoveRequirement(i)}
                          className="hover:opacity-60 transition-opacity"
                          style={{ color: SYSTEM_COLORS.danger }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 px-5 py-4 border-t flex-shrink-0"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold border transition-opacity hover:opacity-70"
              style={{
                borderColor: SYSTEM_COLORS.border,
                color: SYSTEM_COLORS.textMuted,
                backgroundColor: SYSTEM_COLORS.shellSurface,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              {loading ? 'Saving…' : competition ? 'Save Changes' : 'Create Competition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitionModal;
